import { useTranslation } from 'react-i18next';
import {
  getAddOnByVariantId,
  trackAddToCart,
  trackViewCart,
} from 'components/pages/product/helpers';
import { useUpdateLineItemQuantity } from 'typescript/hooks/cart_client';
import { useToaster } from 'typescript/hooks/use_toaster';
import { localizedShopPath } from 'utilities/locales';
import { updateCartCount } from 'utilities/nav';
import { getCountryCode, getLanguageCode } from 'typescript/utilities/locales';

interface UseUpdateItemQuantityProps {
  currentProduct: Record<string, any>;
  cart: Record<string, any>;
  setCart: (cart: Record<string, any>) => void;
  addOns: Record<string, any>;
  onMainProductAdded: () => void;
}

interface OnUpdateItemQuantityParams {
  variantToken: string;
  productName: string;
  quantity: number;
  action?: 'add' | 'overwrite' | 'subtract';
  addOnIds?: string[];
  trackingData?: Record<string, any>;
  bopisLocationId?: string;
  bopisSearchPostalCode?: string;
  showToastNotification?: boolean;
  source?: string;
  onSuccess?: () => void;
}

export const useUpdateItemQuantity = ({
  currentProduct,
  cart,
  setCart,
  addOns,
  onMainProductAdded,
}: UseUpdateItemQuantityProps) => {
  const { t } = useTranslation();
  const { showToast } = useToaster();
  const { updateLineItemQuantity, isPending } = useUpdateLineItemQuantity();

  // log add to cart action to Datadog
  const logAddToCartAction = async (params: {
    status: 'success' | 'error';
    action: 'add' | 'overwrite' | 'subtract';
    errorDetails?: string;
    source?: string;
  }) => {
    const { datadogRum } = await import('@datadog/browser-rum');
    datadogRum.addAction('add-to-cart-pdp-action', {
      ...params,
      productKey: currentProduct?.product_key,
      countryCode: getCountryCode(),
      languageCode: getLanguageCode(),
    });
  };

  const onUpdateItemQuantity = ({
    variantToken,
    productName,
    quantity,
    action = 'add',
    addOnIds = [],
    trackingData,
    bopisLocationId = null,
    bopisSearchPostalCode = null,
    showToastNotification = true,
    source = 'pdp', // examples being the upsell card, or accessory modal, or cta subnav, etc.
    onSuccess,
  }: OnUpdateItemQuantityParams) => {
    const isMainProduct = variantToken === currentProduct.variant_token;

    updateLineItemQuantity(
      {
        variantToken,
        quantity,
        addOnIds,
        action,
        bopisLocationId,
        bopisSearchPostalCode,
      },
      {
        onSuccess: (response) => {
          logAddToCartAction({
            status: 'success',
            action,
            source,
          });

          if (trackingData) {
            trackAddToCart({
              mainProductTrackingData: trackingData,
              addOnsTrackingData: addOnIds?.map(
                (addOnId) => getAddOnByVariantId(addOns, addOnId).tracking_data,
              ),
              cartNumber: cart.number,
              quantity,
            });
          }

          if (isMainProduct && currentProduct.featured_product) {
            onMainProductAdded();
          }

          // update call only returns `subtotal` and `item_count`
          setCart({
            ...cart,
            subtotal: response.subtotal,
            item_count: response.item_count,
          });

          // update the cart item count for public web nav to show in the header
          updateCartCount(response.item_count);

          if (showToastNotification) {
            showToast({
              message: t('productPage.addedToCart', { name: productName }),
              variant: 'success',
              actionLinks: [
                {
                  href: `${localizedShopPath()}/cart`,
                  text: t('productPage.viewCart'),
                  onClick: () => {
                    trackViewCart({
                      orderNumber: cart.number,
                      trackingData:
                        trackingData ?? currentProduct.tracking_data,
                    });
                  },
                },
              ],
            });
          }

          onSuccess?.();
        },
        onError: (error) => {
          logAddToCartAction({
            status: 'error',
            action,
            source,
            errorDetails: error.message,
          });

          showToast({
            message: t('productPage.addToCartError', { name: productName }),
            variant: 'critical',
          });
        },
      },
    );
  };

  return {
    onUpdateItemQuantity,
    isUpdateItemQuantityLoading: isPending,
  };
};
